import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Gift, Check } from 'lucide-react';
import { API, trackEvent } from './peptideContent';

const SIZES = ['S', 'M', 'L', 'XL', '2XL'];
const COLORS = ['Black', 'White', 'Lime'];

export default function BiohackerGiftRedemptionPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [item, setItem] = useState(null);
  const [size, setSize] = useState('M');
  const [color, setColor] = useState('Black');
  const [shipping, setShipping] = useState({ name: '', street: '', city: '', state: '', zip: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    document.title = 'Redeem Your NinjAthlete Gift';
    fetch(`${API}/api/referral/gift/info?token=${encodeURIComponent(token)}`)
      .then(async (r) => { if (!r.ok) throw new Error((await r.json()).detail || 'Invalid link'); return r.json(); })
      .then((d) => {
        if (d.status !== 'available') {
          const msgs = {
            redeemed: 'This gift has already been redeemed.',
            expired: 'This redemption link has expired.',
            cancelled: 'This gift is no longer available.',
            suspended: 'This gift is under review — contact support@ninjathlete.com.',
            pending: 'This gift is not redeemable yet. Redemption opens after the refund period ends.',
          };
          throw new Error(msgs[d.status] || `Gift not redeemable (status: ${d.status})`);
        }
        setInfo(d);
        trackEvent('NA_gift_redemption_started');
      })
      .catch((e) => setError(e.message));
  }, [token]);

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const r = await fetch(`${API}/api/referral/gift/redeem`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, catalog_item_id: item.catalog_item_id, size, color, shipping }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Redemption failed');
      setDone(d);
      trackEvent('NA_gift_redeemed');
    } catch (e) { setError(e.message); } finally { setSubmitting(false); }
  };

  const shippingValid = Object.values(shipping).every((v) => v.trim().length > 1);

  return (
    <div className="bg-[#0a0a0a]" data-testid="gift-redemption-page">
      <div className="max-w-2xl mx-auto px-5 pt-28 pb-24">
        <p className="text-[#C7FA3E] text-xs uppercase tracking-[3px] mb-3 flex items-center gap-2"><Gift className="w-4 h-4" /> Biohacker Member Gift</p>
        <h1 className="text-white text-4xl sm:text-5xl mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Redeem Your Complimentary Item</h1>

        {error && !done && <p className="text-red-400 text-sm border border-red-500/30 bg-red-500/[0.05] px-4 py-3 mb-6" data-testid="gift-error">{error}</p>}

        {done ? (
          <div className="border border-[#C7FA3E]/40 bg-[#C7FA3E]/[0.04] p-8 text-center" data-testid="gift-success">
            <Check className="w-10 h-10 text-[#C7FA3E] mx-auto mb-4" />
            <h2 className="text-white text-2xl mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Gift Redeemed</h2>
            <p className="text-zinc-400 text-sm">Order <strong className="text-white">{done.order_id}</strong> confirmed. Your item ships to the address you provided. Total charged: $0.00.</p>
            <Link to="/shop" className="inline-block mt-6 text-[#C7FA3E] text-xs uppercase tracking-wider hover:underline">Browse the NinjAthlete store →</Link>
          </div>
        ) : info ? (
          <>
            <p className="text-zinc-500 text-xs mb-6">One gift per person and household. Taxes and shipping, if any, are shown before you confirm. This gift has no effect on any clinical evaluation.</p>
            <h2 className="text-white text-sm font-bold uppercase tracking-wider mb-3">1. Choose your item</h2>
            <div className="grid sm:grid-cols-3 gap-3 mb-8">
              {(info.eligible_items || []).map((i) => (
                <button key={i.catalog_item_id} onClick={() => setItem(i)}
                  className={`border p-4 text-left transition-colors ${item?.catalog_item_id === i.catalog_item_id ? 'border-[#C7FA3E] bg-[#C7FA3E]/[0.06]' : 'border-zinc-800 bg-zinc-900/40 hover:border-zinc-600'}`}
                  data-testid={`gift-item-${i.catalog_item_id}`}>
                  <p className="text-white text-sm font-bold">{i.title}</p>
                  <p className="text-zinc-500 text-xs mt-1">Retail ${i.cash_price} · Free with gift</p>
                </button>
              ))}
            </div>
            <h2 className="text-white text-sm font-bold uppercase tracking-wider mb-3">2. Size & color</h2>
            <div className="flex flex-wrap gap-2 mb-3">
              {SIZES.map((s) => (
                <button key={s} onClick={() => setSize(s)} className={`px-3 py-1.5 text-xs border ${size === s ? 'border-[#C7FA3E] text-[#C7FA3E]' : 'border-zinc-700 text-zinc-400'}`} data-testid={`gift-size-${s}`}>{s}</button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 mb-8">
              {COLORS.map((c) => (
                <button key={c} onClick={() => setColor(c)} className={`px-3 py-1.5 text-xs border ${color === c ? 'border-[#C7FA3E] text-[#C7FA3E]' : 'border-zinc-700 text-zinc-400'}`} data-testid={`gift-color-${c}`}>{c}</button>
              ))}
            </div>
            <h2 className="text-white text-sm font-bold uppercase tracking-wider mb-3">3. Shipping address</h2>
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              {[['name', 'Full name'], ['street', 'Street address'], ['city', 'City'], ['state', 'State'], ['zip', 'ZIP code']].map(([k, label]) => (
                <input key={k} value={shipping[k]} onChange={(e) => setShipping({ ...shipping, [k]: e.target.value })}
                  placeholder={label} aria-label={label}
                  className="bg-zinc-900 border border-zinc-700 text-white text-sm px-3 py-2.5 focus:border-[#C7FA3E] outline-none"
                  data-testid={`gift-shipping-${k}`} />
              ))}
            </div>
            <p className="text-zinc-500 text-xs mb-5" data-testid="gift-cost-summary">Item: $0.00 (covered by promotion) · Shipping: $0.00 · Tax: $0.00 · <strong className="text-white">Total: $0.00</strong></p>
            <button onClick={submit} disabled={!item || !shippingValid || submitting}
              className="bg-[#C7FA3E] text-black text-sm font-bold uppercase tracking-wider px-6 py-3.5 disabled:opacity-40 hover:bg-[#d4ff5c] transition-colors"
              data-testid="gift-confirm-btn">
              {submitting ? 'Confirming…' : 'Confirm Free Order'}
            </button>
          </>
        ) : !error ? (
          <p className="text-zinc-500 text-sm">Validating your redemption link…</p>
        ) : null}
      </div>
    </div>
  );
}
